import { useState, useEffect, useReducer, useRef, useCallback } from "react";
import api from "../services/api";
import toastError from "../errors/toastError";
import { agendaReducer, emptyAgenda, createAgendaChangeQueue } from "../helpers/contactAgenda";
const EMPTY_CONTACTS = [];

const useContactAgenda = ({ user, socket, searchParam, selectedTags, filters }) => {
  const [state, send] = useReducer(agendaReducer, undefined, emptyAgenda);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [options, setOptions] = useState({ connections: [], owners: [], queues: [], canReview: false });
  const [resetToken, setResetToken] = useState(0);
  const [loadedKey, setLoadedKey] = useState("");
  const actorKey = JSON.stringify([user.companyId, user.id, user.profile, user.canViewAllContacts]);
  const key = JSON.stringify([actorKey, searchParam, selectedTags, filters, resetToken]);
  const currentKey = useRef(key); currentKey.current = key;
  const sequence = useRef(0); const queue = useRef(null);
  const refresh = useCallback(() => { setResetToken(value => value + 1); }, []);
  useEffect(() => {
    let active = true;
    send({ type: "RESET" }); setPageNumber(1); setLoadedKey("");
    return () => { active = false; };
  }, [key]);
  useEffect(() => {
    let active = true;
    api.get("/contacts/agenda/options").then(({ data }) => { if (active) setOptions({ ...data, scope: actorKey }); }).catch(error => { if (active) toastError(error); });
    return () => { active = false; };
  }, [actorKey, resetToken]);
  useEffect(() => {
    let active = true; setLoading(true);
    const requestSequence = ++sequence.current;
    const params = { searchParam, pageNumber, contactTag: JSON.stringify(selectedTags), ...filters };
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", { params });
        if (!active || currentKey.current !== key) return;
        send({ type: "LOAD", contacts: data.contacts.filter(row => Number(row.companyId) === Number(user.companyId)), count: data.count, sequence: requestSequence });
        setLoadedKey(key); setHasMore(data.hasMore);
      } catch (error) { if (active && currentKey.current === key) toastError(error); }
      finally { if (active && currentKey.current === key) setLoading(false); }
    }, 350);
    return () => { active = false; clearTimeout(timer); };
  }, [key, pageNumber]);
  useEffect(() => {
    let active = true;
    const companyId = user.companyId;
    const feed = createAgendaChangeQueue({
      load: async ids => {
        const requestSequence = ++sequence.current;
        const { data } = await api.get("/contacts/agenda/changes", { params: { ids: JSON.stringify(ids), searchParam, contactTag: JSON.stringify(selectedTags), ...filters } });
        return { ...data, sequence: requestSequence };
      },
      onResult: (ids, result) => {
        if (!active || currentKey.current !== key) return;
        send({ type: "CHANGES", ids, contacts: result.contacts.filter(row => Number(row.companyId) === Number(user.companyId)), count: result.count, sequence: result.sequence });
      },
      onError: error => { if (active && currentKey.current === key) toastError(error); },
      onOverflow: refresh
    });
    queue.current = feed;
    const onContact = data => {
      if (!active || currentKey.current !== key) return;
      if (data.contact?.companyId != null && Number(data.contact.companyId) !== Number(companyId)) return;
      if (["update", "create", "delete", "agenda"].includes(data.action)) feed.add(data.contact?.id || data.contactId);
    };
    const onUser = data => { if (Number(data.user?.id || data.userId) === Number(user.id)) { setOptions(previous => ({ ...previous, canReview: false })); refresh(); } };
    const onFocus = () => refresh();
    socket.on(`company-${companyId}-contact`, onContact);
    socket.on(`company-${companyId}-user`, onUser);
    socket.on("connect", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      active = false; feed.dispose();
      if (queue.current === feed) queue.current = null;
      socket.off(`company-${companyId}-contact`, onContact);
      socket.off(`company-${companyId}-user`, onUser);
      socket.off("connect", onFocus); window.removeEventListener("focus", onFocus);
    };
  }, [key, socket, refresh]);
  const dispatch = useCallback(action => {
    if (action.type === "RESET") { refresh(); return; }
    const id = action.type === "DELETE_CONTACT" ? action.payload : action.payload?.id;
    queue.current?.add(id);
  }, [refresh]);
  return { contacts: loadedKey === key ? state.items : EMPTY_CONTACTS, totalCount: loadedKey === key ? state.count : 0,
    dispatch, pageNumber, setPageNumber, loading, setLoading, hasMore,
    options: options.scope === actorKey ? options : { connections: [], owners: [], queues: [], canReview: false }, refresh };
};
export default useContactAgenda;
